import type { ComboboxRootChangeEventDetails } from "@base-ui/react/combobox";
import { Field } from "@base-ui/react/field";
import type { SelectRootChangeEventDetails } from "@base-ui/react/select";
import { cva } from "class-variance-authority";
import { useEffect, useState } from "react";
import { cn } from "@/lib/utils";
import { FieldLabel } from "../field-label/field-label";
import type { IconName } from "../icon";
import { PlainSelectControl } from "./plain-select-control";
import { SearchableSelectControl } from "./searchable-select-control";

export interface SelectOption {
  value: string;
  label: string;
  icon?: IconName;
  disabled?: boolean;
}

export interface SelectProps {
  /** ID applied to the interactive trigger. Useful for external labels. */
  id?: string;
  /** Available options. */
  options: SelectOption[];
  /** Controlled selected value. */
  value?: string | null;
  /** Callback fired when the selected value changes. Receives null when selection is cleared. */
  onChange?: (
    value: string | null,
    eventDetails: ComboboxRootChangeEventDetails | SelectRootChangeEventDetails,
  ) => void;
  /** Default value for uncontrolled usage. */
  defaultValue?: string | null;
  /** Placeholder text shown when no value is selected. */
  placeholder?: string;
  /** Visual size of the select. Defaults to "md". */
  size?: "sm" | "md";
  /** Label text rendered above the trigger. */
  label?: string;
  /** Tooltip content displayed as an info icon in the label row. */
  tooltip?: string;
  /** Shows a required asterisk in the label row. */
  required?: boolean;
  /** Helper text shown below the trigger. Replaced by error when present. */
  description?: string;
  /** Error message. Presence sets error state; takes precedence over description. */
  error?: string;
  /** Disables the select. Trigger dims to 50% opacity; label and subtext remain opaque. */
  disabled?: boolean;
  /** Whether the select is read-only. */
  readOnly?: boolean;
  /** Reserves vertical space for helper or error text. Defaults to true. */
  reserveSubtextSpace?: boolean;
  /** Name attribute for the hidden form input. */
  name?: string;
  /** Test id applied to the interactive trigger. */
  "data-testid"?: string;
  /** Optional custom content for the selected option in the trigger. */
  renderValue?: (option: SelectOption | undefined) => React.ReactNode;
  /** Optional custom content for an option in the popup. */
  renderOption?: (option: SelectOption) => React.ReactNode;
  /** Popup width. Defaults to matching the trigger. */
  popupWidth?: "anchor" | "content";
  /** Allows filtering options with a search input. Defaults to false. */
  searchable?: boolean;
}

const triggerVariants = cva(
  [
    "flex",
    "items-center",
    "justify-between",
    "gap-(--spacing-4)",
    "w-full",
    "rounded-(--radius-sm)",
    "border",
    "border-solid",
    "border-input-default-outline",
    "bg-input-default-background",
    "text-input-text-default",
    "transition-colors",
    "duration-200",
    "ease-in-out",
    "outline-none",
    "cursor-pointer",
    "data-[disabled]:cursor-not-allowed",
    "[&_svg]:pointer-events-none",
    "[&_svg]:shrink-0",
    "data-[readonly]:bg-input-readonly-background",
    "data-[readonly]:cursor-default",
    "[&:not([data-readonly]):not([data-popup-open])]:hover:bg-input-hover-background",
    "[&:not([data-readonly]):not([data-popup-open])]:hover:border-input-hover-outline",
    "[&:not([data-readonly])[data-popup-open]]:border-input-active-outline",
    "[&:not([data-readonly])]:active:border-input-pressed-outline",
    "[&:not([data-readonly])]:focus-visible:ring-2",
    "[&:not([data-readonly])]:focus-visible:ring-input-focus-ring",
    "[&:not([data-readonly])]:focus-visible:ring-offset-1",
  ],
  {
    variants: {
      size: {
        sm: ["h-(--spacing-32)", "px-(--spacing-10)", "py-(--spacing-6)"],
        md: ["h-(--spacing-40)", "px-(--spacing-10)", "py-(--spacing-10)"],
      },
    },
    defaultVariants: { size: "md" },
  },
);

/**
 * Single-value select field with label, helper text, error state, and icon support.
 *
 * @example
 * <Select
 *   label="Country"
 *   placeholder="Select a country"
 *   options={[{ value: "us", label: "United States" }, { value: "ca", label: "Canada" }]}
 * />
 * <Select label="Priority" options={options} error="Selection is required" />
 */
function Select({
  id,
  options,
  value,
  onChange,
  defaultValue,
  placeholder,
  size = "md",
  label,
  tooltip,
  required,
  description,
  error,
  disabled,
  readOnly = false,
  reserveSubtextSpace = true,
  name,
  "data-testid": dataTestId,
  renderValue,
  renderOption,
  popupWidth = "anchor",
  searchable = false,
}: SelectProps) {
  const hasError = Boolean(error);
  const hasDescription = Boolean(description);
  const showSubtextRow = reserveSubtextSpace || hasError || hasDescription;
  const isControlled = value !== undefined;
  const [internalValue, setInternalValue] = useState<string | null>(
    defaultValue ?? null,
  );
  const [isOpen, setIsOpen] = useState(false);
  const isPopupOpen = isOpen && !readOnly;

  useEffect(() => {
    if (readOnly) setIsOpen(false);
  }, [readOnly]);

  const currentValue = isControlled ? value : internalValue;
  const selectedOption = options.find(
    (option) => option.value === currentValue,
  );

  function handleValueChange(
    nextValue: string | null,
    eventDetails: ComboboxRootChangeEventDetails | SelectRootChangeEventDetails,
  ) {
    const shouldClearSelection =
      eventDetails.reason === "item-press" &&
      !required &&
      currentValue !== null &&
      nextValue === currentValue;

    const resolvedValue = shouldClearSelection ? null : nextValue;

    onChange?.(resolvedValue, eventDetails);

    if (!isControlled && !eventDetails.isCanceled) {
      setInternalValue(resolvedValue);
    }
  }

  const triggerClassName = cn(
    triggerVariants({ size }),
    !hasError && "data-[readonly]:border-input-readonly-outline",
    disabled && "opacity-50",
    hasError && [
      "border-input-error-outline",
      "[&:not([data-readonly]):not([data-popup-open])]:hover:border-input-error-outline",
    ],
  );

  return (
    <Field.Root
      invalid={hasError}
      disabled={disabled}
      className="flex flex-col w-full"
    >
      {label && (
        <FieldLabel label={label} required={required} tooltip={tooltip} />
      )}

      {searchable ? (
        <SearchableSelectControl
          id={id}
          options={options}
          selectedOption={selectedOption}
          onValueChange={handleValueChange}
          open={isPopupOpen}
          onOpenChange={setIsOpen}
          disabled={disabled}
          readOnly={readOnly}
          required={required}
          name={name}
          placeholder={placeholder}
          label={label}
          data-testid={dataTestId}
          renderValue={renderValue}
          renderOption={renderOption}
          popupWidth={popupWidth}
          triggerClassName={triggerClassName}
        />
      ) : (
        <PlainSelectControl
          id={id}
          options={options}
          value={currentValue}
          selectedOption={selectedOption}
          onValueChange={handleValueChange}
          open={isPopupOpen}
          onOpenChange={setIsOpen}
          disabled={disabled}
          readOnly={readOnly}
          required={required}
          name={name}
          placeholder={placeholder}
          label={label}
          data-testid={dataTestId}
          renderValue={renderValue}
          renderOption={renderOption}
          popupWidth={popupWidth}
          triggerClassName={triggerClassName}
        />
      )}

      {showSubtextRow && (
        <div
          data-testid="select-subtext-row"
          className={cn(
            "mt-(--spacing-4)",
            reserveSubtextSpace && "min-h-(--spacing-20)",
          )}
        >
          {hasError ? (
            <Field.Error
              match
              className="text-(length:--font-size-label-small) leading-(--font-line-height-16) text-input-error-text"
            >
              {error}
            </Field.Error>
          ) : description ? (
            <Field.Description className="text-(length:--font-size-label-small) leading-(--font-line-height-16) text-input-text-supporting">
              {description}
            </Field.Description>
          ) : null}
        </div>
      )}
    </Field.Root>
  );
}

export { Select };
